import React, { useState, useEffect } from 'react';
import { X, Download, Eye, Mail, FileText, Calendar, User, Building, MapPin, Loader2 } from 'lucide-react';
import EmailPreviewModal from './EmailPreviewModal';

const LetterGenerator = ({ 
  isOpen, 
  onClose, 
  tenantData,
  unitData,
  propertyData,
  calculationResult, 
  adjustmentType,
  themeClasses 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('commercial');
  const [customGreeting, setCustomGreeting] = useState('');
  const [customClosing, setCustomClosing] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Initialize effective date — for commercial (Gewerbe) default to 1st of next month (no 3-month notice required)
  useEffect(() => {
    if (isOpen) {
      const isCommercial = 
        calculationResult?.tenant?.lease_type === 'commercial' ||
        selectedTemplate === 'commercial';
      const date = new Date();
      if (isCommercial) {
        // Gewerbe: 1. des Folgemonats (automatische Klausel, keine Vorankündigungsfrist)
        date.setMonth(date.getMonth() + 1);
        date.setDate(1);
      } else {
        // Wohnraum: 3 Monate Vorankündigung (§ 558b BGB)
        date.setMonth(date.getMonth() + 3);
        // Round to 1st of that month
        date.setDate(1);
      }
      setEffectiveDate(date.toISOString().split('T')[0]);
    }
  }, [isOpen, selectedTemplate]);

  if (!isOpen || !calculationResult) return null;

  // Extract data for letter generation
  const tenantName = tenantData?.first_name && tenantData?.last_name 
    ? `${tenantData.first_name} ${tenantData.last_name}`
    : tenantData?.name || calculationResult?.tenant?.name || 'Sehr geehrte Damen und Herren';

  const propertyName = 
    propertyData?.property_name || propertyData?.name ||
    calculationResult?.property?.property_name || calculationResult?.property?.name || 
    'Unbekannt';

  const unitInfo = 
    unitData?.unit_identifier || unitData?.unit_number || unitData?.name ||
    calculationResult?.unit?.unit_identifier || calculationResult?.unit?.unit_number || 
    calculationResult?.unit?.identifier || 
    'nicht spezifiziert';

  // Get current rent — prefer cold_rent (Kaltmiete/Grundmiete) over monthly_rent
  const currentRent = parseFloat(
    calculationResult?.tenant?.cold_rent || 
    calculationResult?.tenant?.monthly_rent || 
    calculationResult?.currentRent || 0
  );

  // Get increase amount based on adjustment type
  let increaseAmount = 0;
  if (adjustmentType === 'vpi') {
    increaseAmount = calculationResult?.analysis?.vpi?.increaseAmount || calculationResult?.increaseAmount || 0;
  } else if (adjustmentType === 'mietspiegel') {
    increaseAmount = calculationResult?.analysis?.mietspiegel?.increaseAmount || calculationResult?.increaseAmount || 0;
  } else if (adjustmentType === 'modernisierung') {
    increaseAmount = calculationResult?.analysis?.modernisierung?.increaseAmount || calculationResult?.increaseAmount || 0;
  } else {
    increaseAmount = calculationResult?.increaseAmount || 0;
  }

  const newRent = currentRent + increaseAmount;

  // Determine if commercial lease
  const isCommercialLease = 
    calculationResult?.tenant?.lease_type === 'commercial' ||
    selectedTemplate === 'commercial';

  // Template configurations
  const templates = {
    formal: {
      name: 'Formell',
      greeting: customGreeting || `Sehr geehrte/r ${tenantName}`,
      closing: customClosing || 'Mit freundlichen Grüßen',
      style: 'Sachlich und rechtlich präzise'
    },
    friendly: {
      name: 'Freundlich',
      greeting: customGreeting || `Liebe/r ${tenantName}`,
      closing: customClosing || 'Herzliche Grüße',
      style: 'Persönlich und verständnisvoll'
    },
    commercial: {
      name: 'Gewerbe',
      greeting: customGreeting || `Sehr geehrte Damen und Herren`,
      closing: customClosing || 'Mit freundlichen Grüßen',
      style: 'Geschäftlich und professionell'
    }
  };

  // Generate letter content based on adjustment type and template
  const generateLetterContent = () => {
    const template = templates[selectedTemplate];
    const effectiveDateFormatted = new Date(effectiveDate).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const currentMonth = new Date().toLocaleDateString('de-DE', {
      month: 'long',
      year: 'numeric'
    });

    // VPI index info for commercial letter
    const vpiBaseIndex = calculationResult?.analysis?.vpi?.baseIndex || calculationResult?.vpi?.baseIndex;
    const vpiCurrentIndex = calculationResult?.analysis?.vpi?.currentIndex || calculationResult?.vpi?.currentIndex;
    const vpiPercent = calculationResult?.analysis?.vpi?.increasePercent || calculationResult?.vpi?.increasePercent;
    const vpiBaseDate = calculationResult?.analysis?.vpi?.baseDate || calculationResult?.vpi?.baseDate || 
                        calculationResult?.tenant?.lease_start;

    let letterContent = '';

    // Subject line (Betreff) for commercial
    if (selectedTemplate === 'commercial') {
      letterContent += `Betreff: Mieterhöhung bei vereinbarter Indexmiete\n`;
      letterContent += `Gewerbeeinheit ${unitInfo}, ${propertyName}\n\n`;
    }

    // Greeting
    letterContent += `${template.greeting},\n\n`;

    // Main content based on adjustment type
    if (adjustmentType === 'vpi') {
      if (selectedTemplate === 'commercial') {
        // Professional commercial wording based on real letters
        letterContent += `anbei übersenden wir Ihnen die Berechnung zur Anpassung der Grundmiete auf Basis der vereinbarten Wertsicherungsklausel.\n\n`;
        letterContent += `Ihre Miete beträgt derzeit, ohne Vorauszahlung auf die Betriebskosten, ${currentRent.toFixed(2).replace('.', ',')} €. Auf Grundlage der in Ihrem Mietvertrag vereinbarten Indexierung ergibt sich eine Erhöhung der monatlichen Miete um insgesamt ${increaseAmount.toFixed(2).replace('.', ',')} €.\n\n`;
        letterContent += `Somit ergibt sich ein neuer Grundmietbetrag rückwirkend ab dem ${effectiveDateFormatted} in Höhe von ${newRent.toFixed(2).replace('.', ',')} €.\n\n`;
        if (vpiBaseIndex && vpiCurrentIndex) {
          letterContent += `Berechnungsgrundlage: Verbraucherpreisindex (VPI) Deutschland, Basisjahr 2020 = 100\n`;
          letterContent += `VPI zum Mietvertragsbeginn: ${vpiBaseIndex}\n`;
          letterContent += `Aktueller VPI: ${vpiCurrentIndex}\n`;
          if (vpiPercent) {
            letterContent += `Indexsteigerung: ${typeof vpiPercent === 'number' ? vpiPercent.toFixed(2) : vpiPercent}%\n\n`;
          } else {
            letterContent += `\n`;
          }
        }
        letterContent += `Die Mietanpassung erfolgt auf Grundlage der in Ihrem Mietvertrag vereinbarten Wertsicherungsklausel (Indexmiete). Die Anpassung wird rückwirkend ab dem ${effectiveDateFormatted} wirksam, ohne dass es eines gesonderten Anpassungsverlangens bedarf.\n\n`;
        letterContent += `Wir bitten Sie, Ihren Dauerauftrag entsprechend anzupassen.\n\n`;
        letterContent += `Der Anlage entnehmen Sie unsere Aufstellung zu den aufgelaufenen Rückständen und der neu zu entrichtenden Miete.\n\n`;
      } else {
        // Standard wording for residential
        letterContent += `hiermit teilen wir Ihnen mit, dass sich Ihre Miete für die Einheit ${unitInfo} im Objekt ${propertyName} aufgrund der Entwicklung des Verbraucherpreisindex erhöht.\n\n`;
        letterContent += `Ihre bisherige Miete betrug bis einschließlich ${currentMonth} ${currentRent.toFixed(2).replace('.', ',')} € monatlich.\n\n`;
        letterContent += `Ab dem ${effectiveDateFormatted} erhöht sich Ihre Miete um ${increaseAmount.toFixed(2).replace('.', ',')} € auf ${newRent.toFixed(2).replace('.', ',')} € monatlich.\n\n`;
        letterContent += `Diese Anpassung erfolgt gemäß § 557b BGB (Indexmiete) auf Grundlage der Entwicklung des vom Statistischen Bundesamt veröffentlichten Verbraucherpreisindex für Deutschland.\n\n`;
        // Residential: 3-month notice required (§ 558b BGB)
        letterContent += `Bitte beachten Sie, dass diese Mieterhöhung gemäß § 558b BGB mit einer Frist von drei Monaten zum Ersten eines Monats wirksam wird.\n\n`;
      }
    } else if (adjustmentType === 'mietspiegel') {
      letterContent += `hiermit teilen wir Ihnen mit, dass wir die Miete für Ihre Wohnung in der Einheit ${unitInfo} im Objekt ${propertyName} an die ortsübliche Vergleichsmiete anpassen.\n\n`;
      letterContent += `Ihre bisherige Miete betrug bis einschließlich ${currentMonth} ${currentRent.toFixed(2).replace('.', ',')} € monatlich.\n\n`;
      letterContent += `Ab dem ${effectiveDateFormatted} erhöht sich Ihre Miete um ${increaseAmount.toFixed(2).replace('.', ',')} € auf ${newRent.toFixed(2).replace('.', ',')} € monatlich.\n\n`;
      letterContent += `Diese Anpassung erfolgt gemäß §§ 558 ff. BGB auf Grundlage des qualifizierten Mietspiegels und liegt innerhalb der gesetzlichen Kappungsgrenze.\n\n`;
      letterContent += `Bitte beachten Sie, dass diese Mieterhöhung gemäß § 558b BGB mit einer Frist von drei Monaten zum Ersten eines Monats wirksam wird.\n\n`;
    }

    // Additional notes
    if (additionalNotes.trim()) {
      letterContent += `${additionalNotes.trim()}\n\n`;
    }

    // Closing
    letterContent += `Bei Rückfragen stehen wir Ihnen selbstverständlich jederzeit gerne zur Verfügung.\n\n`;
    letterContent += `${template.closing},\n\n`;
    letterContent += `Ihre Hausverwaltung`;

    return letterContent;
  };

  const letterContent = generateLetterContent();

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    setPdfError(null);
    try {
      const pdfData = {
        tenantName,
        tenantAddress: tenantData?.address || `${propertyName}\nEinheit ${unitInfo}`,
        propertyName,
        unitInfo,
        currentRent,
        increaseAmount,
        newRent,
        effectiveDate: new Date(effectiveDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }),
        adjustmentType,
        letterContent,
        companyInfo: {
          name: 'GRN-Grundbesitz Rhein-Neckar',
          address: 'Krügerstraße 5\n68219 Mannheim',
          phone: '+49 621 123456',
          email: 'info@g-rn.de'
        }
      };
      const response = await fetch('/api/pdf/generate-letter-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pdfData)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mieterhöhungsschreiben_${tenantName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF generation error:', error);
      setPdfError(error.message || 'PDF konnte nicht erstellt werden');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleEmailSend = () => {
    setShowEmailModal(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        {/* Modal container: flex-col so footer is always visible */}
        <div className={`${themeClasses?.cardBg || 'bg-white dark:bg-gray-800'} rounded-lg shadow-xl max-w-7xl w-full flex flex-col`} style={{ maxHeight: '90vh' }}>
          
          {/* Header — fixed */}
          <div className={`flex items-center justify-between p-6 border-b ${themeClasses?.border || 'border-gray-200 dark:border-gray-700'} flex-shrink-0`}>
            <div className="flex items-center">
              <FileText className="w-6 h-6 mr-3 text-blue-600" />
              <h2 className={`text-xl font-semibold ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'}`}>
                Mieterhöhungsschreiben erstellen
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`${themeClasses?.textSecondary || 'text-gray-400'} hover:text-gray-600 dark:hover:text-gray-300`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tenant info bar — fixed */}
          <div className={`px-6 py-3 border-b ${themeClasses?.border || 'border-gray-200 dark:border-gray-700'} flex-shrink-0 grid grid-cols-3 gap-4`}>
            <div>
              <span className={`text-xs ${themeClasses?.textSecondary || 'text-gray-500'}`}>Mieter</span>
              <div className={`text-sm font-medium ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'}`}>{tenantName}</div>
            </div>
            <div>
              <span className={`text-xs ${themeClasses?.textSecondary || 'text-gray-500'}`}>Einheit</span>
              <div className={`text-sm font-medium ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'}`}>{unitInfo}</div>
            </div>
            <div>
              <span className={`text-xs ${themeClasses?.textSecondary || 'text-gray-500'}`}>Erhöhung</span>
              <div className="text-sm font-medium text-green-600">+{increaseAmount.toFixed(2).replace('.', ',')} € → {newRent.toFixed(2).replace('.', ',')} €</div>
            </div>
          </div>

          {/* Content area — scrollable, fills remaining space */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            
            {/* Left Side — Brief-Einstellungen, scrollable */}
            <div className={`w-full lg:w-1/3 p-6 border-r ${themeClasses?.border || 'border-gray-200 dark:border-gray-700'} overflow-y-auto`}>
              <h3 className={`text-lg font-medium ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'} mb-4`}>
                Brief-Einstellungen
              </h3>

              {/* Tenant info */}
              <div className={`${themeClasses?.hoverBg || 'bg-gray-50 dark:bg-gray-700'} rounded-lg p-4 mb-4 space-y-2`}>
                <h4 className={`text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} flex items-center`}>
                  <User className="w-4 h-4 mr-2" /> Mieter-Informationen
                </h4>
                <div className={`text-sm ${themeClasses?.textSecondary || 'text-gray-600 dark:text-gray-400'} space-y-1`}>
                  <div><strong>Name:</strong> {tenantName}</div>
                  <div><strong>Objekt:</strong> {propertyName}</div>
                  <div><strong>Einheit:</strong> {unitInfo}</div>
                  <div><strong>Aktuelle Miete:</strong> {currentRent.toFixed(2).replace('.', ',')} €</div>
                  <div><strong>Neue Miete:</strong> {newRent.toFixed(2).replace('.', ',')} €</div>
                  <div><strong>Erhöhung:</strong> {increaseAmount.toFixed(2).replace('.', ',')} €</div>
                </div>
              </div>

              {/* Template selection */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} mb-2`}>
                  Vorlage auswählen
                </label>
                <div className="space-y-2">
                  {Object.entries(templates).map(([key, template]) => (
                    <label key={key} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="template"
                        value={key}
                        checked={selectedTemplate === key}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className={`text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'}`}>
                          {template.name}
                        </div>
                        <div className={`text-xs ${themeClasses?.textSecondary || 'text-gray-500 dark:text-gray-400'}`}>
                          {template.style}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notice info for commercial */}
              {isCommercialLease && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Gewerbe-Indexmiete:</strong> Die Mietanpassung wird automatisch wirksam (keine 3-monatige Vorankündigungsfrist). Wirksam ab 1. des Folgemonats nach Zugang des Schreibens.
                  </p>
                </div>
              )}

              {/* Effective date */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} mb-1`}>
                  Wirksam ab
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses?.border || 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${themeClasses?.cardBg || 'dark:bg-gray-700'} ${themeClasses?.text || 'dark:text-gray-100'}`}
                />
                <p className={`text-xs ${themeClasses?.textSecondary || 'text-gray-500'} mt-1`}>
                  {isCommercialLease 
                    ? 'Gewerbe: 1. des Folgemonats (automatische Klausel)' 
                    : 'Wohnraum: mind. 3 Monate Vorankündigung (§ 558b BGB)'}
                </p>
              </div>

              {/* Custom greeting */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} mb-1`}>
                  Anrede (optional)
                </label>
                <input
                  type="text"
                  value={customGreeting}
                  onChange={(e) => setCustomGreeting(e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses?.border || 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${themeClasses?.cardBg || 'dark:bg-gray-700'} ${themeClasses?.text || 'dark:text-gray-100'}`}
                  placeholder={templates[selectedTemplate].greeting}
                />
              </div>

              {/* Additional notes */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} mb-1`}>
                  Zusätzliche Hinweise (optional)
                </label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses?.border || 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${themeClasses?.cardBg || 'dark:bg-gray-700'} ${themeClasses?.text || 'dark:text-gray-100'}`}
                  rows="3"
                  placeholder="z.B. Bankverbindung, Rückstandshinweis..."
                />
              </div>

              {/* Custom closing */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses?.text || 'text-gray-700 dark:text-gray-300'} mb-1`}>
                  Grußformel (optional)
                </label>
                <input
                  type="text"
                  value={customClosing}
                  onChange={(e) => setCustomClosing(e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses?.border || 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${themeClasses?.cardBg || 'dark:bg-gray-700'} ${themeClasses?.text || 'dark:text-gray-100'}`}
                  placeholder={templates[selectedTemplate].closing}
                />
              </div>

              {/* PDF Error */}
              {pdfError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <div className="flex items-center">
                    <X className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-800 dark:text-red-200">
                      PDF-Fehler: {pdfError}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side — Live-Vorschau, scrollable */}
            <div className="hidden lg:flex lg:flex-col w-2/3 p-6 overflow-y-auto">
              <h3 className={`text-lg font-medium ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'} mb-4 flex-shrink-0`}>
                Live-Vorschau
              </h3>
              <div className={`flex-1 ${themeClasses?.cardBg || 'bg-white dark:bg-gray-900'} border ${themeClasses?.border || 'border-gray-200 dark:border-gray-700'} rounded-lg p-6 font-mono text-sm ${themeClasses?.text || 'text-gray-900 dark:text-gray-100'} whitespace-pre-wrap overflow-y-auto`}>
                {letterContent}
              </div>
            </div>
          </div>

          {/* Footer — fixed at bottom, always visible */}
          <div className={`flex flex-col sm:flex-row items-center justify-between p-4 border-t ${themeClasses?.border || 'border-gray-200 dark:border-gray-700'} space-y-3 sm:space-y-0 flex-shrink-0`}>
            <div className={`text-sm ${themeClasses?.textSecondary || 'text-gray-500 dark:text-gray-400'}`}>
              Professionelles PDF mit Briefkopf
            </div>
            <div className="flex flex-row space-x-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 ${themeClasses?.textSecondary || 'text-gray-700 dark:text-gray-300'} border ${themeClasses?.border || 'border-gray-300 dark:border-gray-600'} rounded-md hover:bg-gray-50 dark:hover:bg-gray-700`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isGeneratingPDF ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    PDF wird erstellt...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    PDF herunterladen
                  </>
                )}
              </button>
              <button
                onClick={handleEmailSend}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Als E-Mail versenden
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        briefContent={letterContent}
        tenantData={tenantData}
        calculationResult={calculationResult}
        adjustmentType={adjustmentType}
      />
    </>
  );
};

export default LetterGenerator;
